<template>

    <v-container :id="'widgets-page'" class="fill-height" style="display: block;" fluid>

        <div id="control-panel" v-if="!$store.state.expressMode">
            <div class="dashboard-select-container">
                <v-autocomplete id="db-select" class="dashboard-select" outlined label="Select Dashboard" color="#c42111" :items="dashboardFavs" v-model="selectedDashboardAttempt" return-object item-text="dashboard_name" @change="applyDashboardClick">
                    <template v-slot:item="{item}">
                        <span v-text="item.dashboard_name" />
                        <span class="dashboard-select-default-text" v-text="'DEFAULT'" v-if="item.default" />
                    </template>
                </v-autocomplete>
            </div>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" class="ml-0 mt-2 add-widget-button" title="Dashboard Management" icon @click="showDashboardManagement = true"><v-icon>mdi-monitor-multiple</v-icon></v-btn>
                </template>
                <span>Dashboard Management</span>
            </v-tooltip>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" class="ml-0 mt-2 add-widget-button" title="Save Dashboard" icon @click="saveDashboardClick">
                        <v-icon>mdi-content-save</v-icon>
                        <v-tooltip bottom color="rgba(48, 48, 48, 1.0)" v-if="$store.state.isDirty">
                            <template v-slot:activator="{ on }">
                                <v-icon small class="is-dirty-save-button-icon" v-on="on">mdi-information-outline</v-icon>
                            </template>
                            <span>Pending Changes</span>
                        </v-tooltip>
                    </v-btn>
                </template>
                <span>Save Dashboard</span>
            </v-tooltip>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" class="ml-0 mt-2 add-widget-button" title="Clear Dashboard" icon @click="clearDashboard"><v-icon>mdi-layers-remove</v-icon></v-btn>
                </template>
                <span>Clear Dashboard</span>
            </v-tooltip>

            <v-divider class="ml-3 mr-2" vertical />

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" class="ml-0 mt-2 add-widget-button" title="Add Maps Widget" icon @click="addMapWidget" :disabled="mapWidgetExists || !$store.state.batchConfig.webapp_geomap"><v-icon>mdi-google-maps</v-icon></v-btn>
                </template>
                <span>Maps Widget</span>
            </v-tooltip>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" class="ml-1 mt-2 add-widget-button" title="Add Alarms Widget" icon @click="addAlarmWidget" :disabled="!$store.state.batchConfig.webapp_alarms"><v-icon>mdi-bell-ring</v-icon></v-btn>
                </template>
                <span>Alarms Widget</span>
            </v-tooltip>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" class="ml-1 mt-2 add-widget-button" title="Add Issues Widget" icon @click="addIssueWidget" :disabled="!$store.state.batchConfig.webapp_issues"><v-icon>mdi-sticker-alert</v-icon></v-btn>
                </template>
                <span>Issues Widget</span>
            </v-tooltip>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" class="ml-1 mt-2 add-widget-button" title="Add Report Widget" icon @click="showReportWidgetsList = true" :disabled="!$store.state.batchConfig.webapp_reports"><v-icon>mdi-chart-bar</v-icon></v-btn>
                </template>
                <span>Report Widget</span>
            </v-tooltip>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" class="ml-1 mt-2 add-widget-button" title="Add Tableau Widget" icon @click="addTableauWidget" :disabled="!$store.state.batchConfig.tableau"><v-icon>mdi-cloud</v-icon></v-btn>
                </template>
                <span>Tableau Widget</span>
            </v-tooltip>
            
            <v-divider class="ml-2 mr-2" vertical />

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" class="ml-0 mt-2 add-widget-button" title="Fullscreen" icon @click="toggleFullscreen"><v-icon>mdi-fullscreen</v-icon></v-btn>
                </template>
                <span>Fullscreen</span>
            </v-tooltip>

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" class="ml-1 mt-2 add-widget-button" title="Auto-snap Widgets" icon @click="autoSnapWidgets" :disabled="!widgets.length"><v-icon>mdi-apps</v-icon></v-btn>
                </template>
                <span>Auto-snap Widgets</span>
            </v-tooltip>

            <v-divider class="ml-3 mr-2" vertical />

            <v-tooltip bottom>
                <template v-slot:activator="{ on, attrs }">
                    <v-btn v-bind="attrs" v-on="on" class="ml-1 mt-2 add-widget-button" title="Manage Dashbaord Subscriptions" icon @click="manageSubscriptions" :disabled="!isCustomerAdmin || !selectedDashboard || !selectedDashboard.dashboard_id"><v-icon>mdi-email-sync</v-icon></v-btn>
                </template>
                <span>Manage Subscriptions</span>
            </v-tooltip>

        </div>

        <div id="widgets-area" :class="{express: $store.state.expressMode}">
            <widget v-for="(widget, idx) in widgets" :widgetObj="widget" :key="`${widget.id}`" :windowResize="windowResize" :widgetIdx="idx" :totalWidgets="widgets.length" :autoSnap="autoSnap" @change="handleWidgetChange" @deleteWidgetClick="deleteWidgetClick(widget)" />
            <span id="no-widgets-text" v-if="!widgets.length && !selectedDashboard && !$store.state.expressMode">Add widgets or open a dashboard</span>
        </div>

        <v-dialog v-model="showReportWidgetsList" max-width="80%" width="1400px" persistent v-if="showReportWidgetsList" @keydown="(k) => {k.key == 'Escape' ? showReportWidgetsList = false : null}">
            <report-widget-list :reportWidgets="reportWidgets" @close="showReportWidgetsList = false" @add="addReportWidget"></report-widget-list>
        </v-dialog>

        <v-dialog v-model="showDashboardFavsDetails" max-width="80%" width="550px" persistent v-if="showDashboardFavsDetails" @keydown="(k) => {k.key == 'Escape' ? showDashboardFavsDetails = false : null}">
            <dashboard-favs-details :selectedDashboard="selectedDashboard" :dashboards="dashboardList" @cancel="showDashboardFavsDetails = false" @new="saveNewDashboard" @overwrite="overwriteDashboard"></dashboard-favs-details>
        </v-dialog>

        <v-dialog v-model="showDashboardManagement" max-width="80%" width="1400px" persistent v-if="showDashboardManagement" @keydown="(k) => {k.key == 'Escape' ? showDashboardManagement = false : null}">
            <dashboard-management :dashboards="dashboardList" @close="showDashboardManagement = false" @open="(dashboard) => {this.selectedDashboardAttempt = dashboard; applyDashboardClick();}" @delete="handleDashboardDelete"></dashboard-management>
        </v-dialog>

        <v-dialog v-model="showSubscriptionManagement" max-width="80%" width="1650px" persistent v-if="showSubscriptionManagement" @keydown="(k) => {k.key == 'Escape' ? showSubscriptionManagement = false : null}">
            <subscription-management subscriptionType="dashboard" :subscriptionTypeId="selectedDashboard.dashboard_id" @close="showSubscriptionManagement = false" />
        </v-dialog>

    </v-container>

</template>

<script>
import ReportWidgetList from '@/components/TelsasoftDashboard/ReportWidgetList';
import DashboardManagement from '@/components/TelsasoftDashboard/DashboardManagement';
import DashboardFavsDetails from '@/components/TelsasoftDashboard/DashboardFavsDetails';
import SubscriptionManagement from '@/components/TelsasoftDashboard/SubscriptionManagement';
import Widget from '@/components/TelsasoftDashboard/Widget';
export default {
    name: 'TelsasoftDashboard',
    props: ['expressViewConditions'],
    components: {
        ReportWidgetList,
        DashboardManagement,
        DashboardFavsDetails,
        SubscriptionManagement,
        Widget
    },
    data: () => ({
        selectedDashboardAttempt: null,
        selectedDashboard: null,
        widgets: [],
        widgetsHash: null,
        alarmViews: [],
        issueViews: [],
        reportWidgets: [],
        tableauInfo: null,
        selectedReportWidget: null,
        showReportWidgetsList: false,
        newCount: 0,
        showDashboardManagement: false,
        dashboardList: [],
        showDashboardFavsDetails: false,
        windowResizeFunc: null,
        windowResize: 0,
        autoSnap: 0,
        showSubscriptionManagement: false
    }),

    computed: {
        dashboardFavs: function() {
            return this.dashboardList.length ? this.dashboardList.filter(d => {return d.fav}) : [];
        },

        mapWidgetExists: function() {
            return this.widgets.some(w => {return w.type == 'geomap';});
        },

        isCustomerAdmin: function() {
            return (this.$store.state.user.user_data.is_customer_admin);
        },
    },

    methods: {
        addMapWidget: function() {
            if(!this.checkLicense()) {
                return;
            }
            this.widgets.push({
                id: `${Date.now()}`,
                name: 'Map Widget',
                type: 'geomap',
                icon: 'mdi-google-maps',
                views: [],
                selectedView: null,
                selectedDensity: null,
                widgetOffsetLeftPct: (10 + (5 * this.newCount)),
                widgetOffsetTopPct: (10 + (5 * this.newCount)),
                widgetWidthPct: 30,
                widgetHeightPct: 40
            });
            this.newCount++;
            this.$store.commit('setIsDirty', true);
        },

        addAlarmWidget: function() {
            if(!this.checkLicense()) {
                return;
            }
            this.widgets.push({
                id: `${Date.now()}`,
                name: 'Alarm Widget',
                type: 'alarms',
                icon: 'mdi-bell-ring',
                views: this.alarmViews,
                selectedView: null,
                selectedDensity: null,
                widgetOffsetLeftPct: (10 + (5 * this.newCount)),
                widgetOffsetTopPct: (10 + (5 * this.newCount)),
                widgetWidthPct: 30,
                widgetHeightPct: 40
            });
            this.newCount++;
            this.$store.commit('setIsDirty', true);
        },

        addIssueWidget: function() {
            if(!this.checkLicense()) {
                return;
            }
            this.widgets.push({
                id: `${Date.now()}`,
                name: 'Issue Widget',
                type: 'issues',
                icon: 'mdi-sticker-alert',
                views: this.issueViews,
                selectedView: null,
                selectedDensity: null,
                widgetOffsetLeftPct: (10 + (5 * this.newCount)),
                widgetOffsetTopPct: (10 + (5 * this.newCount)),
                widgetWidthPct: 30,
                widgetHeightPct: 40
            });
            this.newCount++;
            this.$store.commit('setIsDirty', true);
        },

        addReportWidget: function(reportWidget) {
            if(!this.checkLicense()) {
                return;
            }
            this.getReportViews(reportWidget.report_name).then(results => {
                this.widgets.push({
                    id: `${Date.now()}`,
                    name: `${reportWidget.widget_name}`,
                    type: 'report',
                    icon: 'mdi-chart-bar',
                    views: results,
                    selectedView: null,
                    selectedDensity: null,
                    showTypeNum: 0,
                    chartOptions: {},
                    reportDetails: reportWidget,
                    widgetOffsetLeftPct: (10 + (5 * this.newCount)),
                    widgetOffsetTopPct: (10 + (5 * this.newCount)),
                    widgetWidthPct: 30,
                    widgetHeightPct: 40
                });
                this.showReportWidgetsList = false;
                this.newCount++;
                this.$store.commit('setIsDirty', true);
            });
        },

        addTableauWidget: function() {
            if(!this.checkLicense()) {
                return;
            }
            this.widgets.push({
                id: `${Date.now()}`,
                name: 'Tableau Widget',
                type: 'tableau',
                icon: 'mdi-cloud',
                views: [],
                selectedView: null,
                selectedDensity: null,
                selectedTableauTab: null,
                tableauTabs: this.tableauInfo.tableau_views,
                tableauRootUrl: this.tableauInfo.root_url,
                tableauUrlParams: this.tableauInfo.url_params,
                widgetOffsetLeftPct: (10 + (5 * this.newCount)),
                widgetOffsetTopPct: (10 + (5 * this.newCount)),
                widgetWidthPct: 30,
                widgetHeightPct: 40
            });
            this.newCount++;
            this.$store.commit('setIsDirty', true);
        },

        checkLicense: function() {
            let allowedWidgets = this.$store.state.batchConfig.webapp_dashboard;
            if(this.widgets.length >= allowedWidgets) {
                this.$store.commit('showConfirmModal', {
                    title: 'License Limit',
                    message: `Your current dashboard license allows for <b>1</b> dashboard with a max of <b>${allowedWidgets}</b> widgets. \nPlease contact <a href="mailto:support@telsasoft.com" style="color: #c42111;">support</a> for more information.`,
                    type: 'confirm',
                    hideCancel: true,
                    confirmAction: () => {}
                });
                return false;
            }
            return true;
        },

        getReportViews: function(reportName) {
            let route = `${TELSA.apiHost}views/list/report|${encodeURIComponent(encodeURIComponent(reportName))}`;
            return this.$http.get(route).then(response => {
                let results = response.body;
                return results;
            });
        },

        deleteWidgetClick: function(widget) {
            this.$store.commit('showConfirmModal', {
                title: 'Confirm Delete',
                message: `Are you sure you want to delete <b>${widget.name}</b>?`,
                type: 'delete',
                hideCancel: false,
                confirmAction: () => this.deleteWidget(widget)
            });
        },
        deleteWidget: function(widget) {
            this.widgets.splice(this.widgets.indexOf(widget), 1);
            this.$store.commit('setIsDirty', true);
        },

        toggleFullscreen: function() {
            document.getElementById('widgets-area').requestFullscreen();
        },

        getAlarmViews: function() {
            let route = `${TELSA.apiHost}views/list/alarm`;
            return this.$http.get(route).then(response => {
                let results = response.body;
                this.alarmViews = results;
            });
        },

        getIssueViews: function() {
            let route = `${TELSA.apiHost}views/list/issue`;
            return this.$http.get(route).then(response => {
                let results = response.body;
                this.issueViews = results;
            });
        },

        getReportWidgets: function() {
            let route = `${TELSA.apiHost}report/widgets/list`;
            return this.$http.get(route).then(response => {
                let results = response.body;
                this.reportWidgets = results;
            });
        },

        getDashboardList: function() {
            let route = `${TELSA.apiHost}dashboard/list`;
            return this.$http.get(route).then(response => {
                let results = response.body;
                this.dashboardList = results;
                if(this.$store.state.expressMode) {
                    if(!this.dashboardList.length) {
                        TELSA.loading(false);
                        this.$store.commit('showConfirmModal', {
                            title: 'Dashboard Not Found',
                            message: `This dashbaord could not be found. Perhaps it was recently deleted.`,
                            type: 'warning',
                            hideCancel: true,
                            confirmAction: function(){window.close();}
                        });
                    }
                    else {
                        this.selectedDashboard = this.dashboardList[0]
                    }
                }
                else {
                    this.selectedDashboard = this.dashboardList.find(d => {return d.default}) || (this.dashboardFavs.length ? this.dashboardFavs[0] : null);
                }
                this.selectedDashboardAttempt = this.selectedDashboard;
                this.applyDashboardClick();
                TELSA.loading(false);
            });
        },

        saveDashboardClick: function() {
            this.showDashboardFavsDetails = true;
        },

        saveNewDashboard: function(newDashboard) {
            // Check their license. Using "5" maxAllowedWidgets to determine if they're restricted to just 1 dashboard. Actual max dashbaords would noramlly be 2. 
            let allowedWidgets = this.$store.state.batchConfig.webapp_dashboard;
            if(allowedWidgets <= 5 && this.dashboardList.length >= 1) {
                this.$store.commit('showConfirmModal', {
                    title: 'License Limit',
                    message: `Your current dashboard license allows for <b>1</b> dashboard with a max of <b>${allowedWidgets}</b> widgets. \nPlease contact <a href="mailto:support@telsasoft.com" style="color: #c42111;">support</a> for more information.`,
                    type: 'confirm',
                    hideCancel: true,
                    confirmAction: () => {}
                });
                return false;
            }

            TELSA.loading(true);
            this.showDashboardFavsDetails = false;

            // If they set this new dashboard as the new default, then we have to unset the old one.
            if(newDashboard.dashboardDefault) {
                for(let dashboard of this.dashboardList) {
                    dashboard.default = false;
                }    
            }

            this.selectedDashboard = {
                dashboard_id: 0,
                user_id: this.$store.state.user.user_id,
                dashboard_name: newDashboard.dashboardName,
                dashboard_device_type: this.$store.state.user.device_type,
                dashboard_public: newDashboard.dashboardPublic,
                dashboard_config: {widgets: this.formatWidgets()},
                fav: true,
                default: newDashboard.dashboardDefault
            };
            this.dashboardList.push(this.selectedDashboard);
            let route = `${TELSA.apiHost}dashboard/add`;
            return this.$http.post(route, {args: {dashboard: this.selectedDashboard}}).then(response => {
                let result = response.body;
                this.selectedDashboard.dashboard_id = result;
                this.saveDashboardFavs();
                this.widgetsHash = TELSA.hash(this.widgets);
                this.$store.commit('setIsDirty', false);
                TELSA.loading(false);
            });
        },

        overwriteDashboard: function() {
            TELSA.loading(true);
            this.showDashboardFavsDetails = false;
            this.selectedDashboard.dashboard_config = {widgets: this.formatWidgets()};
            let route = `${TELSA.apiHost}dashboard/edit`;
            return this.$http.post(route, {args: {dashboard: this.selectedDashboard}}).then(response => {
                let results = response.body;
                this.saveDashboardFavs();
                this.widgetsHash = TELSA.hash(this.widgets);
                this.$store.commit('setIsDirty', false);
                TELSA.loading(false);
            });
        },

        formatWidgets: function() {
            return this.widgets.map(w => {
                delete w.views;
                if(w.chartOptions && w.chartOptions.series) {
                    delete w.chartOptions.series;
                }
                if(w.chartOptions && w.chartOptions.xAxis && w.chartOptions.xAxis.categories) {
                    delete w.chartOptions.xAxis.categories;
                }
                if(w.chartOptions && w.chartOptions.lowThresExceeded) {
                    delete w.chartOptions.lowThresExceeded;
                }
                if(w.chartOptions && w.chartOptions.highThresExceeded) {
                    delete w.chartOptions.highThresExceeded;
                }
                if(w.tableauTabs) {
                    delete w.tableauTabs;
                }
                return w;
            });
        },

        saveDashboardFavs: function() {
            let dashboardFavs = [];
            for(let dashboard of this.dashboardList) {
                if(dashboard.fav) {
                    dashboardFavs.push({
                        dashboard_id: dashboard.dashboard_id,
                        default: dashboard.default
                    });
                }
            }

            let route = `${TELSA.apiHost}dashboard/favs`;
            return this.$http.post(route, {args: {dashboard_favs: dashboardFavs}}).then(response => {
                let result = response.body;
                TELSA.loading(false);
            });
        },

        applyDashboardClick() {
            if(this.$store.state.isDirty) {
                this.$store.commit('showConfirmModal', {
                    title: 'Are you sure?',
                    message: 'Are you sure you want to abandon your changes without saving?',
                    type: 'warning',
                    cancelAction: () => {
                        this.selectedDashboardAttempt = this.selectedDashboard;
                    },
                    confirmAction: () => {this.selectedDashboard = this.selectedDashboardAttempt; this.applyDashboard();}
                });
            }
            else {
                this.selectedDashboard = this.selectedDashboardAttempt;
                this.applyDashboard();
                return;
            }
        },

        applyDashboard: function() {
            this.$store.commit('setIsDirty', false);
            let reportWidgetViewsPromises = [];
            this.widgets = [];
            this.$nextTick(function() {
                if(!this.selectedDashboard) {
                    return;
                }
                let dashboardName = this.selectedDashboard.dashboard_name;
                let widgetClones = JSON.parse(JSON.stringify(this.selectedDashboard.dashboard_config.widgets));
                for(let widget of widgetClones) {
                    if(widget.type == 'report') {
                        reportWidgetViewsPromises.push(
                            this.getReportViews(widget.reportDetails.report_name).then(results => {
                                if(dashboardName != this.selectedDashboard.dashboard_name) {
                                    // This means they changed the dashbaord before the report widget(s) got their views
                                    return;
                                }
                                widget.views = results;
                                this.widgets.push(widget);
                            })
                        );
                    }
                    else if(widget.type == 'tableau') {
                        widget.views = [];
                        widget.tableauTabs = this.tableauInfo.tableau_views,
                        this.widgets.push(widget);
                    }
                    else {
                        if(widget.type == 'geomap') {widget.views = []}
                        else if(widget.type == 'alarms') {widget.views = this.alarmViews}
                        else if(widget.type == 'issues') {widget.views = this.issueViews}
                        this.widgets.push(widget);
                    }
                }

                Promise.all(reportWidgetViewsPromises).then(v => {
                    this.widgetsHash = TELSA.hash(this.widgets);
                });
            });
        },

        handleDashboardDelete: function(dashboard) {
            if(dashboard == this.selectedDashboard) {
                this.clearDashboard();
            }
        },

        clearDashboard: function() {
            this.selectedDashboard = null;
            this.widgets = [];
        },

        getTableauDashboardInfo: function() {
            let route = `${TELSA.apiHost}dashboard/tableau/info`;
            return this.$http.post(route, {args: {tableauCreds: this.$store.state.batchConfig.tableau}}).then(response => {
                let result = response.body;
                this.tableauInfo = result;
            });
        },

        autoSnapWidgets: function() {
            this.autoSnap++;
        },

        handleWidgetChange: function() {
            let previousHash = this.widgetsHash;
            let newHash = TELSA.hash(this.widgets);
            if(previousHash != newHash) {
                this.$store.commit('setIsDirty', true);
            }
        },

        manageSubscriptions: function() {
            this.showSubscriptionManagement = true;
        }
    },

    mounted() {
        TELSA.loading(true);
        let promises = [];
        promises.push(this.getAlarmViews());
        promises.push(this.getIssueViews());
        promises.push(this.getReportWidgets());
        promises.push(this.getTableauDashboardInfo());
        Promise.all(promises).then(v => {
            this.getDashboardList();
        });


        let self = this;
        this.windowResizeHandler = function() {
            self.windowResize++;
        }
        window.addEventListener('resize', this.windowResizeHandler);
    },

    created() {
    },

    destroyed() {
        window.removeEventListener('resize', this.windowResizeHandler);
    }
};
</script>

<style scoped>

    #control-panel {
        height: 50px;
        background-color: #424242;
        border: gray solid 1px;
        border-radius: 1px;
        width: 100%;
        box-shadow: 4px 4px 5px 0px rgba(0,0,0,1);
        position: relative;
    }

    #control-panel .add-widget-button {
        vertical-align: top;
    }

    .dashboard-select-container {
        position: relative;
        display: inline-block;
        text-align: center;
        height: 40px;
        vertical-align: top;
        margin-top: 7px;
        margin-right: 10px;
        margin-left: 10px;
    }
    .dashboard-select {
        width: 280px;
        display: inline-block;
    }
    .dashboard-select-container .view-button {
        display: initial;
    }

    .dashboard-select-default-text {
        color: yellow;
        position: absolute;
        right: 20px;
        text-align: right;
        font-size: 11px;
        font-weight: bold;
    }

    .is-dirty-save-button-icon {
        right: -6px;
        top: -12px;
    }

    .report-widget-select-container {
        margin-top: 5px;
        display: inline-block;
        position: relative;
        height: 15px;
        vertical-align: top;
    }
    .report-widget-select {
        display: inline-block;
        width: 300px;
    }
    .report-widget-select-button {
        width: 40px !important;
        min-width: 40px !important;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        vertical-align: inherit;
        height: 38px;
        min-height: 38px;
    }

    #widgets-area {
        width: 100%;
        overflow: hidden;
        position: relative;
        border-radius: 5px;
        border: rgba(255, 255, 255, 0.12) solid 1px;
        border-radius: 5px;
        background-color: #303030;

        height: calc(100% - 60px);
        margin-top: 16px;
    }
    #widgets-area.express {
        height: 100%;
        margin-top: 0px;
    }

    #widgets-area-visible {
        width: 100%;
        height: calc(100% - 42px);
        bottom: 0;
        position: absolute;
        border: rgba(255, 255, 255, 0.12) solid 1px;
        border-radius: 5px;
        background-color: #303030;
    }

    #widgets-area #no-widgets-text {
        position: absolute;
        top: calc(50% - (48px / 1));
        left: calc(50% - (478px / 2));
        font-size: 32px;
        text-align: center;
        color: rgba(255, 255, 255, 0.5);
        display: block;
    }

</style>

<style>

    .report-widget-select-container .v-text-field.v-text-field--outlined .v-input__control {
        min-height: 10px !important;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
    }
    .report-widget-select-container .v-text-field--outlined > .v-input__control > .v-input__slot {
        min-height: 10px !important;
    }
    .report-widget-select-container .v-text-field.v-text-field--enclosed .v-input__prepend-outer, .v-text-field.v-text-field--enclosed .v-input__prepend-inner, .v-text-field.v-text-field--enclosed .v-input__append-inner, .v-text-field.v-text-field--enclosed .v-input__append-outer {
        margin-top: 7px !important;
    }
    .report-widget-select-container .v-select.v-text-field--outlined:not(.v-text-field--single-line) .v-select__selections {
        padding-top: 4px !important;
        padding-bottom: 2px !important;
    }
    .report-widget-select-container .v-text-field--outlined .v-label {
        top: 11px !important;
    }
    .report-widget-select-container .v-text-field--outlined .v-label--active {
        top: 19px !important;
    }


    .dashboard-select-container .v-text-field.v-text-field--outlined .v-input__control{
        min-height: 10px !important;
    }
    .dashboard-select-container .v-text-field--outlined > .v-input__control > .v-input__slot {
        min-height: 10px !important;
    }
    .dashboard-select-container .v-text-field.v-text-field--enclosed .v-input__prepend-outer, .v-text-field.v-text-field--enclosed .v-input__prepend-inner, .v-text-field.v-text-field--enclosed .v-input__append-inner, .v-text-field.v-text-field--enclosed .v-input__append-outer {
        margin-top: 7px !important;
    }
    .dashboard-select-container .v-select.v-text-field--outlined:not(.v-text-field--single-line) .v-select__selections {
        padding-top: 4px !important;
        padding-bottom: 2px !important;
    }
    .dashboard-select-container .v-text-field--outlined .v-label {
        top: 11px !important;
    }
    .dashboard-select-container .v-text-field--outlined .v-label--active {
        top: 17px !important;
    }

</style>



